import { useEffect, useId, useRef, useState, type FormEvent, type KeyboardEvent } from 'react';
import { useNavigate } from 'react-router-dom';
import styled from 'styled-components';
import {
  apiErrorText,
  useApprovePrcVersionMutation,
  useDeletePrcVersionMutation,
  useExportPrcXlsxMutation,
  useRefreshPrcModelsMutation,
  useRejectPrcVersionMutation,
  useSubmitPrcVersionMutation,
  type PrcVersionPayload,
} from '@shared/api';
import { Button } from '@shared/ui/Button';
import { IconDownload, IconRefresh } from '@shared/ui/icons';
import { fleetLeading, fleetType, formatCount, typo } from '@entities/fleet';
import {
  approvalBlockedForAuthor,
  canDeleteVersion,
  formatIsoDate,
  isEditableStatus,
  localIsoDate,
  recalcSummary,
  type RecalcSummary,
} from '@entities/prc';
import { Control, ControlArea } from '@widgets/FleetRegistry/formFields';
import { Notice, TextButton } from '@widgets/FleetRegistry/ui';
import type { PrcAccess } from '../access';
import { describedBy, FormField } from '../drawerParts';
import { MODELS_GEN, count, personName, safeFileName, saveObjectUrl } from '../format';
import { NARROW, RecalcNotice } from '../ui';

type Panel = 'submit' | 'approve' | 'reject' | 'delete';

type Flash =
  | { kind: 'recalc'; lead: string; summary: RecalcSummary }
  | { kind: 'text'; tone: 'success' | 'info' | 'danger'; text: string; reread?: boolean };

const Row = styled.div`
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: ${({ theme }) => theme.spacing(2)};
  margin-top: ${({ theme }) => theme.spacing(5)};

  & > .spacer {
    flex: 1 1 auto;
  }

  @container prc (max-width: ${NARROW}) {
    & > button {
      flex: 1 1 100%;
    }

    & > .spacer {
      display: none;
    }
  }
`;

const PanelBox = styled.form`
  margin-top: ${({ theme }) => theme.spacing(3)};
  padding: ${({ theme }) => `${theme.spacing(4)} ${theme.spacing(4)} ${theme.spacing(1)}`};
  max-width: 720px;
  border: 1px solid ${({ theme }) => theme.colors.borderStrong};
  border-radius: ${({ theme }) => theme.radius.lg};
  background: ${({ theme }) => theme.colors.backgroundElevated};

  & > h3 {
    margin: 0 0 ${({ theme }) => theme.spacing(2)};
    font-size: ${fleetType.lead};
    font-weight: 600;
    line-height: ${fleetLeading.tight};

    &:focus,
    &:focus-visible {
      outline: none;
      box-shadow: none;
    }
  }

  & > p {
    margin: 0 0 ${({ theme }) => theme.spacing(4)};
    font-size: ${fleetType.body};
    line-height: ${fleetLeading.normal};
    color: ${({ theme }) => theme.colors.text.secondary};
    text-wrap: pretty;
  }

  & > .buttons {
    display: flex;
    flex-wrap: wrap;
    gap: ${({ theme }) => theme.spacing(2)};
    margin-bottom: ${({ theme }) => theme.spacing(3)};
  }
`;

const DateControl = styled(Control)`
  max-width: 220px;
`;

const Reason = styled.p`
  flex: 1 1 100%;
  margin: ${({ theme }) => `${theme.spacing(2)} 0 0`};
  font-size: ${fleetType.small};
  color: ${({ theme }) => theme.colors.text.secondary};
`;

const FlashBox = styled.div`
  margin-top: ${({ theme }) => theme.spacing(3)};
`;

const PanelError = styled.div`
  margin: ${({ theme }) => `0 0 ${theme.spacing(4)}`};
`;

interface Props {
  payload: PrcVersionPayload;
  access: PrcAccess;
  /** Несохранённых правок во вкладке — действия с версией ждут сохранения. */
  dirty: number;
  onCopy: () => void;
  onCompare: () => void;
  onReread: () => void;
}

/**
 * Действия с версией по статусу и правам: отправить, утвердить с датой
 * «действует с», вернуть с причиной, удалить черновик, обновить паспорта,
 * создать копию, выгрузить в Excel. Ответ сервера — его текстом.
 */
export function VersionActions({ payload, access, dirty, onCopy, onCompare, onReread }: Props) {
  const v = payload.version;
  const navigate = useNavigate();
  const uid = useId();
  const [panel, setPanel] = useState<Panel | null>(null);
  const [flash, setFlash] = useState<Flash | null>(null);
  const [panelError, setPanelError] = useState<string | null>(null);
  const [comment, setComment] = useState('');
  const [validFrom, setValidFrom] = useState(() => localIsoDate(new Date()));
  const [touched, setTouched] = useState(false);
  const headingRef = useRef<HTMLHeadingElement>(null);
  const openerRef = useRef<HTMLButtonElement | null>(null);

  const [submit, submitState] = useSubmitPrcVersionMutation();
  const [approve, approveState] = useApprovePrcVersionMutation();
  const [reject, rejectState] = useRejectPrcVersionMutation();
  const [remove, removeState] = useDeletePrcVersionMutation();
  const [refresh, refreshState] = useRefreshPrcModelsMutation();
  const [exportXlsx, exportState] = useExportPrcXlsxMutation();
  const busy =
    submitState.isLoading ||
    approveState.isLoading ||
    rejectState.isLoading ||
    removeState.isLoading ||
    refreshState.isLoading;

  const editable = isEditableStatus(v.status);
  const ready = payload.models.filter((m) => m.selected && !m.error).length;
  const variants = payload.variants ?? [];
  const variantsWithoutObjects = variants.filter((x) => x.objects.length === 0);
  // Куда запишутся цены вариантов — для панели утверждения.
  const variantsLine =
    variants.length > 0
      ? ` По вариантам условий — на их объекты: ${variants
          .map(
            (x) =>
              `«${x.name}» — ${x.objects.length > 0 ? x.objects.map((o) => `«${o.name}»`).join(', ') : 'объекты не выбраны'}`,
          )
          .join('; ')}.`
      : '';
  const canEdit = editable && access.canWrite;
  const canDecide = v.status === 'submitted' && access.isApprover;
  const authorBlocked = approvalBlockedForAuthor(v, access.userId, access.approveOwn);
  const canDelete = access.canWrite && canDeleteVersion(v, access.userId, access.roles);
  const waitSave = dirty > 0 ? 'Сначала сохраните или отмените правки во вкладке' : undefined;

  useEffect(() => {
    if (panel) headingRef.current?.focus({ preventScroll: false });
  }, [panel]);

  function openPanel(next: Panel, opener: HTMLButtonElement) {
    openerRef.current = opener;
    setPanelError(null);
    setTouched(false);
    setComment('');
    if (next === 'approve') setValidFrom(localIsoDate(new Date()));
    setFlash(null);
    setPanel(next);
  }

  function closePanel() {
    setPanel(null);
    requestAnimationFrame(() => openerRef.current?.focus());
  }

  function onPanelKey(e: KeyboardEvent<HTMLFormElement>) {
    if (e.key === 'Escape') {
      e.preventDefault();
      closePanel();
    }
  }

  async function run(e: FormEvent) {
    e.preventDefault();
    setTouched(true);
    setPanelError(null);
    try {
      if (panel === 'submit') {
        await submit({ id: v.id, comment: comment.trim() || undefined }).unwrap();
        setFlash({
          kind: 'text',
          tone: 'success',
          text: 'Версия отправлена на утверждение. Править её можно будет, если вернут на доработку.',
        });
      } else if (panel === 'approve') {
        if (!validFrom) return;
        const res = await approve({
          id: v.id,
          valid_from: validFrom,
          comment: comment.trim() || undefined,
        }).unwrap();
        const a = res.approve;
        setFlash({
          kind: 'text',
          tone: 'success',
          text: a
            ? typo(
                `Утверждено. В справочник техники записана ПРЦ ${count(a.approved, MODELS_GEN)} с ${formatIsoDate(validFrom)}${a.variants ? ` — по основному расчёту и вариантам условий (${a.variants}) на их объекты` : ''}; прежних цен закрыто днём раньше — ${formatCount(a.closed)}.${a.skipped_errors > 0 ? ` Пропущено отмеченных моделей с ошибкой расчёта: ${a.skipped_errors}.` : ''}${a.superseded > 0 ? ` Заменено прежних версий: ${a.superseded}.` : ''}`,
              )
            : 'Версия утверждена.',
        });
      } else if (panel === 'reject') {
        if (!comment.trim()) return;
        await reject({ id: v.id, comment: comment.trim() }).unwrap();
        setFlash({
          kind: 'text',
          tone: 'info',
          text: 'Версия возвращена на доработку — автор увидит причину в шапке версии.',
        });
      } else if (panel === 'delete') {
        await remove(v.id).unwrap();
        navigate('/agents/econ/prc', {
          replace: true,
          state: { prcFlash: `Черновик «${v.label}» удалён.` },
        });
        return;
      }
      setPanel(null);
    } catch (err) {
      setPanelError(apiErrorText(err, 'Сервер не выполнил действие.'));
    }
  }

  async function doRefresh() {
    setFlash(null);
    try {
      const res = await refresh({
        id: v.id,
        expected_updated_at: v.updated_at,
        variant_id: payload.variant?.id,
      }).unwrap();
      const r = res.refresh;
      const lead = r
        ? `Паспорта обновлены: моделей добавлено ${r.added}, убрано ${r.removed}, с изменённым паспортом ${r.changed}`
        : 'Паспорта обновлены';
      setFlash({ kind: 'recalc', lead, summary: recalcSummary(payload.models, res.models) });
    } catch (err) {
      setFlash({
        kind: 'text',
        tone: 'danger',
        text: apiErrorText(err, 'Не удалось обновить паспорта из справочника.'),
        reread: true,
      });
    }
  }

  async function doExport() {
    setFlash(null);
    try {
      const variant = payload.variant ?? null;
      const { url, name } = await exportXlsx({ id: v.id, variant: variant?.id }).unwrap();
      const label = variant ? `${v.label} — ${variant.name}` : v.label;
      saveObjectUrl(url, name ?? `${safeFileName(`ПРЦ ${label}`)}.xlsx`);
    } catch (err) {
      setFlash({
        kind: 'text',
        tone: 'danger',
        text: apiErrorText(err, 'Не удалось выгрузить расчёт.'),
      });
    }
  }

  const reasonMissing = panel === 'reject' && touched && !comment.trim();
  const dateMissing = panel === 'approve' && touched && !validFrom;

  return (
    <>
      <Row>
        {canEdit && (
          <>
            <Button
              type="button"
              $variant="primary"
              disabled={busy || dirty > 0}
              title={waitSave}
              aria-expanded={panel === 'submit'}
              onClick={(e) => openPanel('submit', e.currentTarget)}
            >
              Отправить на утверждение
            </Button>
            <Button
              type="button"
              $variant="secondary"
              disabled={busy || dirty > 0}
              title={
                waitSave ??
                'Подтянуть из справочника техники текущие паспорта: новые модели, выбывшие и изменённые паспорта'
              }
              onClick={() => void doRefresh()}
            >
              <IconRefresh size={16} />
              {refreshState.isLoading ? 'Обновляю…' : 'Обновить паспорта'}
            </Button>
          </>
        )}
        {canDecide && (
          <>
            <Button
              type="button"
              $variant="primary"
              disabled={busy || authorBlocked}
              aria-describedby={authorBlocked ? `${uid}-four-eyes` : undefined}
              aria-expanded={panel === 'approve'}
              onClick={(e) => openPanel('approve', e.currentTarget)}
            >
              Утвердить
            </Button>
            <Button
              type="button"
              $variant="secondary"
              disabled={busy}
              aria-expanded={panel === 'reject'}
              onClick={(e) => openPanel('reject', e.currentTarget)}
            >
              Вернуть на доработку
            </Button>
          </>
        )}
        {access.canWrite && (
          <Button
            type="button"
            $variant="secondary"
            disabled={busy || dirty > 0}
            title={waitSave}
            onClick={onCopy}
          >
            Создать копию
          </Button>
        )}
        <span className="spacer" />
        <Button
          type="button"
          $variant="ghost"
          disabled={exportState.isLoading}
          title={
            payload.variant
              ? `Лист «Расчет машино-час» в условиях варианта «${payload.variant.name}»`
              : 'Лист «Расчет машино-час»; у утверждённой версии — с грифом «УТВЕРЖДАЮ»'
          }
          onClick={() => void doExport()}
        >
          <IconDownload size={16} />
          {exportState.isLoading ? 'Выгружаю…' : 'Выгрузить в Excel'}
        </Button>
        {canDelete && (
          <Button
            type="button"
            $variant="ghost"
            disabled={busy || dirty > 0}
            title={waitSave}
            aria-expanded={panel === 'delete'}
            onClick={(e) => openPanel('delete', e.currentTarget)}
          >
            Удалить черновик
          </Button>
        )}
        {dirty > 0 && (canEdit || access.canWrite) && (
          <Reason>
            {typo(
              'Во вкладке есть несохранённые правки — сохраните или отмените их, потом отправляйте, копируйте или удаляйте версию.',
            )}
          </Reason>
        )}
        {canDecide && authorBlocked && (
          <Reason id={`${uid}-four-eyes`}>
            {typo(
              'Вы создали или отправили эту версию — утверждает другой сотрудник. Вернуть на доработку можно.',
            )}
          </Reason>
        )}
      </Row>

      {panel && (
        <PanelBox
          onSubmit={(e) => void run(e)}
          onKeyDown={onPanelKey}
          noValidate
          aria-labelledby={`${uid}-panel-h`}
        >
          <h3 id={`${uid}-panel-h`} ref={headingRef} tabIndex={-1}>
            {panel === 'submit'
              ? 'Отправить на утверждение'
              : panel === 'approve'
                ? 'Утвердить версию'
                : panel === 'reject'
                  ? 'Вернуть на доработку'
                  : 'Удалить черновик'}
          </h3>
          {panel === 'submit' && (
            <p>
              {typo(
                `К утверждению отмечено ${count(payload.summary.selected, MODELS_GEN)} из ${formatCount(payload.summary.models)}. Модели с ошибкой расчёта не утверждаются. Пока версия на утверждении, её не правят.${variants.length > 0 ? ` Утверждаются и варианты условий (${variants.length}) — на свои объекты.` : ''}${variantsWithoutObjects.length > 0 ? ` У варианта ${variantsWithoutObjects.map((x) => `«${x.name}»`).join(', ')} не выбраны объекты — отправить не получится.` : ''}`,
              )}
            </p>
          )}
          {panel === 'approve' && (
            <p>
              {typo(
                `ПРЦ ${count(ready, MODELS_GEN)} — отмеченных и посчитанных без ошибки — запишется в справочник техники с этой даты ${v.objects.length > 0 ? `для ${v.objects.length === 1 ? 'объекта' : 'объектов'} ${v.objects.map((o) => `«${o.name}»`).join(', ')}; прежняя цена каждой модели на ${v.objects.length === 1 ? 'этом объекте' : 'этих объектах'} закроется днём раньше, цены других объектов не изменятся` : 'как общая — для объектов без своей ПРЦ; прежняя общая цена каждой модели закроется днём раньше'}.${variantsLine} Задним числом цену не переписать: дата должна быть позже действующей.`,
              )}
            </p>
          )}
          {panel === 'reject' && (
            <p>{typo('Автор увидит причину в шапке версии и сможет снова править черновик.')}</p>
          )}
          {panel === 'delete' && (
            <p>
              {typo(
                `Черновик «${v.label}» удалится вместе с расчётом всех моделей. Отменить удаление нельзя.`,
              )}
              {v.created_by !== access.userId &&
                typo(
                  ` Автор черновика — ${personName(v.created_by_name)}: черновик пропадёт и у автора.`,
                )}
            </p>
          )}

          {panelError && (
            <PanelError>
              <Notice $tone="danger" role="alert">
                <span className="text">{panelError}</span>
              </Notice>
            </PanelError>
          )}

          {panel === 'approve' && (
            <FormField
              id={`${uid}-date`}
              label="Действует с"
              required
              error={dateMissing ? 'Укажите дату, с которой действует цена' : null}
            >
              <DateControl
                id={`${uid}-date`}
                type="date"
                value={validFrom}
                aria-required="true"
                aria-invalid={dateMissing ? true : undefined}
                aria-describedby={describedBy(`${uid}-date`, false, dateMissing)}
                onChange={(e) => setValidFrom(e.target.value)}
              />
            </FormField>
          )}
          {panel !== 'delete' && (
            <FormField
              id={`${uid}-comment`}
              label={panel === 'reject' ? 'Что поправить' : 'Комментарий'}
              required={panel === 'reject'}
              error={reasonMissing ? 'Напишите, что поправить, — без причины не вернуть' : null}
            >
              <ControlArea
                id={`${uid}-comment`}
                value={comment}
                rows={3}
                aria-required={panel === 'reject' ? true : undefined}
                aria-invalid={reasonMissing ? true : undefined}
                aria-describedby={describedBy(`${uid}-comment`, false, reasonMissing)}
                placeholder={
                  panel === 'reject'
                    ? 'Например: цена ДТ — по договору с ноября 152 ₽/л'
                    : undefined
                }
                onChange={(e) => setComment(e.target.value)}
              />
            </FormField>
          )}

          <div className="buttons">
            <Button
              type="submit"
              $variant={panel === 'delete' ? 'danger' : 'primary'}
              disabled={busy}
            >
              {busy
                ? 'Выполняю…'
                : panel === 'submit'
                  ? 'Отправить'
                  : panel === 'approve'
                    ? validFrom
                      ? `Утвердить с ${formatIsoDate(validFrom)}`
                      : 'Утвердить'
                    : panel === 'reject'
                      ? 'Вернуть на доработку'
                      : 'Удалить черновик'}
            </Button>
            <Button type="button" $variant="ghost" disabled={busy} onClick={closePanel}>
              Отмена
            </Button>
          </div>
        </PanelBox>
      )}

      {flash && (
        <FlashBox>
          {flash.kind === 'recalc' ? (
            <RecalcNotice
              summary={flash.summary}
              lead={flash.lead}
              onCompare={onCompare}
              onDismiss={() => setFlash(null)}
            />
          ) : (
            <Notice $tone={flash.tone} role={flash.tone === 'danger' ? 'alert' : 'status'}>
              <span className="text">{flash.text}</span>
              {flash.reread && (
                <TextButton type="button" onClick={onReread}>
                  Перечитать версию
                </TextButton>
              )}
              <TextButton
                type="button"
                onClick={() => setFlash(null)}
                aria-label="Скрыть сообщение"
              >
                Скрыть
              </TextButton>
            </Notice>
          )}
        </FlashBox>
      )}
    </>
  );
}
